import type { ProductDetails } from './shopify.types';

/**
 * Lecture des metafields produit : étiquettes (`custom`), caractéristiques
 * chiffrées (`conseil`) et avis (`reviews`).
 *
 * Deux règles tiennent tout ce fichier :
 *
 * 1. `metafields(identifiers:)` répond un tableau **positionnel** contenant des
 *    `null` pour les metafields absents. On ré-indexe donc sur `namespace`/`key`
 *    au lieu de se fier au rang, qui n'est pas garanti.
 * 2. Toutes les valeurs sont des **chaînes JSON**. Une valeur illisible laisse
 *    le champ absent, elle ne fait jamais échouer le produit entier.
 *
 * `custom.odeur` n'est pas demandé : il existe côté admin mais le Storefront le
 * renvoie null, il n'est pas exposé.
 */

/** Metafields demandés au Storefront, dans l'ordre de la fiche produit. */
export const DETAIL_METAFIELDS: readonly { namespace: string; key: string }[] = [
  { namespace: 'custom', key: 'taille' },
  { namespace: 'custom', key: 'duree_de_mastication' },
  { namespace: 'custom', key: 'description_produit_courte' },
  // Textes rédigés produit par produit, qui composent la fiche en clair de la
  // boutique. Ils ne sont pas tous renseignés partout : les packs les ont tous,
  // un jouet n'en a souvent aucun.
  { namespace: 'custom', key: 'notre_garantie' },
  { namespace: 'custom', key: 'que_contient_ce_pack_' },
  { namespace: 'custom', key: 'pour_quel_type_de_chien' },
  { namespace: 'custom', key: 'conseils_d_utilisation_pack' },
  { namespace: 'conseil', key: 'hardness' },
  { namespace: 'conseil', key: 'duration' },
  { namespace: 'conseil', key: 'digestion' },
  { namespace: 'conseil', key: 'fat_level' },
  { namespace: 'conseil', key: 'min_age_months' },
  { namespace: 'conseil', key: 'min_weight_kg' },
  { namespace: 'conseil', key: 'suitable_for_gulpers' },
  { namespace: 'conseil', key: 'chew_family' },
  { namespace: 'conseil', key: 'proteins' },
  { namespace: 'reviews', key: 'rating' },
  { namespace: 'reviews', key: 'rating_count' },
];

/** Fragment GraphQL correspondant, construit depuis la liste ci-dessus. */
export const METAFIELDS_FIELD = `metafields(identifiers: [${DETAIL_METAFIELDS.map(
  ({ namespace, key }) => `{namespace: "${namespace}", key: "${key}"}`,
).join(', ')}]) { namespace key value }`;

export interface RawMetafield {
  namespace?: string | null;
  key?: string | null;
  value?: string | null;
}

export type RawMetafields = (RawMetafield | null | undefined)[] | null | undefined;

/**
 * `namespace.key` → valeur brute. Les trous du tableau positionnel et les
 * entrées sans identifiant sont écartés.
 */
export function indexMetafields(raw: RawMetafields): Map<string, string> {
  const index = new Map<string, string>();
  for (const field of raw ?? []) {
    if (!field?.namespace || !field.key || typeof field.value !== 'string') continue;
    index.set(`${field.namespace}.${field.key}`, field.value);
  }
  return index;
}

/** `"[\"Petit\",\"Moyen\"]"` → `['Petit', 'Moyen']`. Illisible ou vide → `[]`. */
export function parseTextList(value: string | undefined): string[] {
  if (value === undefined) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

/** `"5"` → `5`. Tout ce qui n'est pas un entier reste absent. */
export function parseInteger(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const text = value.trim();
  if (text === '') return undefined;
  const parsed = Number(text);
  return Number.isInteger(parsed) ? parsed : undefined;
}

/** `"true"` → `true`, `"false"` → `false`, le reste est absent. */
export function parseBoolean(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  const text = value.trim().toLowerCase();
  if (text === 'true') return true;
  if (text === 'false') return false;
  return undefined;
}

/** Champ texte simple, vidé de ses espaces. Une chaîne vide reste absente. */
export function parseText(value: string | undefined): string | undefined {
  const text = value?.trim();
  return text === undefined || text === '' ? undefined : text;
}

/**
 * `{"scale_min":"1.0","scale_max":"5.0","value":"4.5"}` → `4.5`.
 * Seule la valeur nous intéresse : l'échelle est toujours 1 à 5 en boutique.
 */
export function parseRating(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined;
  const raw = (parsed as { value?: unknown }).value;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  const note = Number(raw.trim());
  return Number.isFinite(note) ? note : undefined;
}

/** Nœuds qui empilent des blocs : leurs enfants sont séparés par un saut de ligne. */
const BLOCK_CONTAINERS = new Set(['root', 'list']);

function nodeToText(node: unknown): string {
  if (typeof node !== 'object' || node === null) return '';
  const { type, value, children } = node as {
    type?: unknown;
    value?: unknown;
    children?: unknown;
  };

  if (type === 'text') return typeof value === 'string' ? value : '';

  const parts = (Array.isArray(children) ? children : []).map(nodeToText);
  if (typeof type === 'string' && BLOCK_CONTAINERS.has(type)) {
    return parts.filter((part) => part.trim() !== '').join('\n');
  }
  // Paragraphe, titre, lien, fragment en gras : les morceaux se recollent sans
  // rien ajouter, sinon « Riche en protéines » perdrait ses espaces voisins.
  return parts.join('');
}

/**
 * Rich text Shopify → texte brut. Les emojis et les sauts de ligne écrits par
 * la boutique sont conservés ; chaque bloc devient une ligne. Un JSON invalide
 * renvoie une chaîne vide, jamais une exception.
 */
export function richTextToPlainText(json: string): string {
  if (typeof json !== 'string' || json.trim() === '') return '';
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return '';
  }

  return nodeToText(parsed)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Construit les caractéristiques d'un produit. Renvoie `undefined` quand aucun
 * metafield n'est lisible : la fiche n'a alors rien de plus à montrer.
 */
export function toProductDetails(raw: RawMetafields): ProductDetails | undefined {
  const fields = indexMetafields(raw);
  if (fields.size === 0) return undefined;

  const durete = parseInteger(fields.get('conseil.hardness'));
  const duree = parseInteger(fields.get('conseil.duration'));
  const digestion = parseInteger(fields.get('conseil.digestion'));
  const matiereGrasse = parseInteger(fields.get('conseil.fat_level'));
  const ageMinimumMois = parseInteger(fields.get('conseil.min_age_months'));
  const poidsMinimumKg = parseInteger(fields.get('conseil.min_weight_kg'));
  const pourGloutons = parseBoolean(fields.get('conseil.suitable_for_gulpers'));
  const famille = parseText(fields.get('conseil.chew_family'));
  const note = parseRating(fields.get('reviews.rating'));
  const nombreAvis = parseInteger(fields.get('reviews.rating_count'));
  const descriptionCourte = richTextToPlainText(
    fields.get('custom.description_produit_courte') ?? '',
  );
  const garantie = richTextToPlainText(fields.get('custom.notre_garantie') ?? '');
  const contenu = richTextToPlainText(fields.get('custom.que_contient_ce_pack_') ?? '');
  const pourQuelChien = richTextToPlainText(fields.get('custom.pour_quel_type_de_chien') ?? '');
  const conseils = richTextToPlainText(fields.get('custom.conseils_d_utilisation_pack') ?? '');

  return {
    tailles: parseTextList(fields.get('custom.taille')),
    dureeMastication: parseTextList(fields.get('custom.duree_de_mastication')),
    proteines: parseTextList(fields.get('conseil.proteins')),
    ...(durete !== undefined ? { durete } : {}),
    ...(duree !== undefined ? { duree } : {}),
    ...(digestion !== undefined ? { digestion } : {}),
    ...(matiereGrasse !== undefined ? { matiereGrasse } : {}),
    ...(ageMinimumMois !== undefined ? { ageMinimumMois } : {}),
    ...(poidsMinimumKg !== undefined ? { poidsMinimumKg } : {}),
    ...(pourGloutons !== undefined ? { pourGloutons } : {}),
    ...(famille !== undefined ? { famille } : {}),
    ...(note !== undefined ? { note } : {}),
    ...(nombreAvis !== undefined ? { nombreAvis } : {}),
    ...(descriptionCourte !== '' ? { descriptionCourte } : {}),
    ...(garantie !== '' ? { garantie } : {}),
    ...(contenu !== '' ? { contenu } : {}),
    ...(pourQuelChien !== '' ? { pourQuelChien } : {}),
    ...(conseils !== '' ? { conseils } : {}),
  };
}
